import React, { forwardRef } from 'react';
import {
  Box,
  Alert,
  Button,
  Chip,
  IconButton,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import {
  Search,
  Clear,
  Info,
  Warning,
  Error as ErrorIcon,
  CheckCircle,
  Close,
} from '@mui/icons-material';
import { glassTheme } from './GlassTheme';

// Glass Background Container - Main page wrapper with gradient
export const GlassBackground = forwardRef(({ children, ...props }, ref) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  return (
    <Box
      ref={ref}
      sx={{
        minHeight: '100vh',
        width: '100vw',
        background: glassTheme.gradients.primary,
        backgroundAttachment: 'fixed',
        position: 'relative',
        overflowY: 'auto',
        overflowX: 'hidden',
        ...props.sx,
      }}
      {...props}
    >
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: isMobile ? 'flex-start' : 'center',
          justifyContent: 'center',
          padding: isMobile
            ? glassTheme.spacing.mobile.padding
            : glassTheme.spacing.desktop.padding,
          paddingTop: isMobile
            ? glassTheme.spacing.mobile.paddingLarge
            : glassTheme.spacing.desktop.paddingLarge,
          paddingBottom: isMobile
            ? glassTheme.spacing.mobile.paddingLarge
            : glassTheme.spacing.desktop.paddingLarge,
        }}
      >
        {children}
      </Box>
    </Box>
  );
});

// Glass Container - Main content container with glass effect
export const GlassContainer = forwardRef(({ children, variant = 'default', ...props }, ref) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const variants = {
    default: {
      borderRadius: glassTheme.borderRadius.xlarge,
      padding: isMobile
        ? glassTheme.spacing.mobile.paddingLarge
        : glassTheme.spacing.desktop.paddingLarge,
    },
    compact: {
      borderRadius: glassTheme.borderRadius.large,
      padding: isMobile
        ? glassTheme.spacing.mobile.padding
        : glassTheme.spacing.desktop.padding,
    },
    loginCard: {
      borderRadius: '16px',
      padding: isMobile ? '16px' : '32px',
      width: isMobile ? '100%' : '440px',
      minWidth: isMobile ? 'auto' : '440px',
      maxWidth: isMobile ? '400px' : '440px',
      overflow: 'hidden',
      color: 'white',
    },
  };

  return (
    <Box
      ref={ref}
      className="glass-container"
      sx={{
        position: 'relative',
        border: glassTheme.glass.border,
        background: glassTheme.glass.background,
        backdropFilter: glassTheme.glass.backdropFilter,
        boxShadow: glassTheme.glass.shadow,
        ...variants[variant],
        ...props.sx,
      }}
      {...props}
    >
      {children}
    </Box>
  );
});

// Glass Card - Individual card component
export const GlassCard = forwardRef(({
  children,
  variant = 'default',
  clickable = false,
  disabled = false,
  ...props
}, ref) => {
  const variants = {
    default: {
      background: glassTheme.glass.background,
    },
    admin: {
      background: glassTheme.gradients.admin,
    },
    deceased: {
      background: 'rgba(255, 255, 255, 0.05)',
      opacity: 0.85,
      filter: 'grayscale(50%)',
    },
  };

  const hoverStyles = clickable && !disabled ? {
    cursor: 'pointer',
    transition: `all ${glassTheme.transitions.standard} ${glassTheme.transitions.easing}`,
    '&:hover': {
      transform: 'translateY(-4px) scale(1.02)',
      boxShadow: glassTheme.glass.shadowHover,
      background: variant === 'admin'
        ? glassTheme.gradients.adminHover
        : glassTheme.glass.backgroundHover,
    },
  } : {};

  const { sx, onClick, ...otherProps } = props;

  return (
    <Box
      ref={ref}
      className="glass-card"
      onClick={disabled ? undefined : onClick}
      sx={{
        height: '100%',
        borderRadius: glassTheme.borderRadius.large,
        border: glassTheme.glass.border,
        backdropFilter: glassTheme.glass.backdropFilter,
        boxShadow: glassTheme.glass.shadowLight,
        overflow: 'hidden',
        position: 'relative',
        display: 'flex',
        flexDirection: 'column',
        opacity: disabled ? 0.5 : 1,
        pointerEvents: disabled ? 'none' : 'auto',
        ...variants[variant],
        ...hoverStyles,
        ...sx,
      }}
      {...otherProps}
    >
      {children}
    </Box>
  );
});

// Glass Alert - Alert component with glass effect
export const GlassAlert = forwardRef(({ severity = 'info', children, variant = 'default', ...props }, ref) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const severityStyles = {
    error: {
      background: 'rgba(255, 255, 255, 0.1)',
      border: '1px solid rgba(211, 47, 47, 0.5)',
      icon: '#ef5350',
    },
    info: {
      background: 'rgba(255, 255, 255, 0.1)',
      border: '1px solid rgba(2, 136, 209, 0.5)',
      icon: '#29b6f6',
    },
    success: {
      background: 'rgba(255, 255, 255, 0.1)',
      border: '1px solid rgba(46, 125, 50, 0.5)',
      icon: '#66bb6a',
    },
    warning: {
      background: 'rgba(255, 255, 255, 0.1)',
      border: '1px solid rgba(237, 108, 2, 0.5)',
      icon: '#ffa726',
    },
  };

  const styles = severityStyles[severity];

  // Different variants for different contexts
  const marginBottom = variant === 'login' ? 2 : 4;

  return (
    <Alert
      ref={ref}
      severity={severity}
      variant="standard"
      sx={{
        ...props.sx,
        mb: marginBottom,
        background: `${styles.background} !important`,
        backdropFilter: 'blur(12px) !important',
        border: `${styles.border} !important`,
        borderRadius: `${glassTheme.borderRadius.medium} !important`,
        color: 'rgba(255, 255, 255, 0.95) !important',
        boxShadow: '0 2px 12px rgba(0, 0, 0, 0.3) !important',
        padding: '8px 16px !important',
        '&.MuiAlert-root': {
          background: `${styles.background} !important`,
          border: `${styles.border} !important`,
          color: 'rgba(255, 255, 255, 0.95) !important',
        },
        '&.MuiAlert-standardError, &.MuiAlert-standardInfo, &.MuiAlert-standardWarning, &.MuiAlert-standardSuccess': {
          background: `${styles.background} !important`,
          border: `${styles.border} !important`,
          color: 'rgba(255, 255, 255, 0.95) !important',
        },
        '&.MuiAlert-filledError, &.MuiAlert-filledInfo, &.MuiAlert-filledWarning, &.MuiAlert-filledSuccess': {
          background: `${styles.background} !important`,
          border: `${styles.border} !important`,
          color: 'rgba(255, 255, 255, 0.95) !important',
        },
        '&.MuiAlert-outlinedError, &.MuiAlert-outlinedInfo, &.MuiAlert-outlinedWarning, &.MuiAlert-outlinedSuccess': {
          background: `${styles.background} !important`,
          border: `${styles.border} !important`,
          color: 'rgba(255, 255, 255, 0.95) !important',
        },
        '& .MuiAlert-icon': {
          color: `${styles.icon} !important`,
        },
        '& .MuiSvgIcon-root': {
          color: `${styles.icon} !important`,
        },
        '& .MuiAlert-message': {
          fontWeight: 400,
          fontSize: isMobile
            ? glassTheme.fontSize.mobile.body2
            : glassTheme.fontSize.desktop.body2,
          color: 'rgba(255, 255, 255, 0.95) !important',
          '& *': {
            color: 'rgba(255, 255, 255, 0.95) !important',
          },
          '& strong': {
            color: 'rgba(255, 255, 255, 1) !important',
            fontWeight: 600,
          },
          '& .MuiTypography-root': {
            color: 'rgba(255, 255, 255, 0.95) !important',
          },
          '& .MuiTypography-h6': {
            color: 'rgba(255, 255, 255, 1) !important',
            fontWeight: 600,
          },
          '& h6': {
            color: 'rgba(255, 255, 255, 1) !important',
            fontWeight: 600,
          },
          '& ul, & ol': {
            color: 'rgba(255, 255, 255, 0.95) !important',
          },
          '& li': {
            color: 'rgba(255, 255, 255, 0.95) !important',
          },
          '& p': {
            color: 'rgba(255, 255, 255, 0.95) !important',
          },
        },
      }}
      {...props}
    >
      {children}
    </Alert>
  );
});

// Glass Info Card - Standardized info card with optional dismiss button
export const GlassInfoCard = forwardRef(({ severity = 'info', children, icon, onClose, ...props }, ref) => {
  const severityStyles = {
    error: {
      background: 'rgba(255, 255, 255, 0.1)',
      border: '1px solid rgba(211, 47, 47, 0.5)',
      iconColor: '#ef5350',
      icon: icon || <ErrorIcon sx={{ color: '#ef5350', flexShrink: 0 }} />,
    },
    info: {
      background: 'rgba(255, 255, 255, 0.1)',
      border: '1px solid rgba(2, 136, 209, 0.5)',
      iconColor: '#29b6f6',
      icon: icon || <Info sx={{ color: '#29b6f6', flexShrink: 0 }} />,
    },
    success: {
      background: 'rgba(255, 255, 255, 0.1)',
      border: '1px solid rgba(46, 125, 50, 0.5)',
      iconColor: '#66bb6a',
      icon: icon || <CheckCircle sx={{ color: '#66bb6a', flexShrink: 0 }} />,
    },
    warning: {
      background: 'rgba(255, 255, 255, 0.1)',
      border: '1px solid rgba(237, 108, 2, 0.5)',
      iconColor: '#ffa726',
      icon: icon || <Warning sx={{ color: '#ffa726', flexShrink: 0 }} />,
    },
  };

  const styles = severityStyles[severity];

  return (
    <Box
      ref={ref}
      sx={{
        mb: 2,
        p: 2,
        background: styles.background,
        backdropFilter: 'blur(12px)',
        border: styles.border,
        borderRadius: glassTheme.borderRadius.medium,
        boxShadow: '0 2px 12px rgba(0, 0, 0, 0.3)',
        display: 'flex',
        gap: 1.5,
        alignItems: 'flex-start',
        ...props.sx,
      }}
      {...props}
    >
      {styles.icon}
      <Box sx={{ flex: 1, color: 'rgba(255, 255, 255, 0.95)' }}>
        {typeof children === 'string' ? (
          <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.95)' }}>
            {children}
          </Typography>
        ) : (
          children
        )}
      </Box>
      {onClose && (
        <IconButton
          size="small"
          onClick={onClose}
          sx={{
            color: 'rgba(255, 255, 255, 0.7)',
            '&:hover': {
              color: 'rgba(255, 255, 255, 0.9)',
              background: 'rgba(255, 255, 255, 0.1)',
            },
          }}
        >
          <Close fontSize="small" />
        </IconButton>
      )}
    </Box>
  );
});

// Glass Warning Card - Enhanced warning card with title and content sections
export const GlassWarningCard = forwardRef(({
  severity = 'warning',
  title,
  children,
  icon,
  ...props
}, ref) => {
  const severityConfig = {
    info: {
      color: '#29b6f6',
      icon: icon || <Info sx={{ color: '#29b6f6', mt: 0.25, flexShrink: 0 }} />,
    },
    warning: {
      color: '#ffa726',
      icon: icon || <Warning sx={{ color: '#ffa726', mt: 0.25, flexShrink: 0 }} />,
    },
    error: {
      color: '#ef5350',
      icon: icon || <ErrorIcon sx={{ color: '#ef5350', mt: 0.25, flexShrink: 0 }} />,
    },
    success: {
      color: '#66bb6a',
      icon: icon || <CheckCircle sx={{ color: '#66bb6a', mt: 0.25, flexShrink: 0 }} />,
    },
  };

  const config = severityConfig[severity];

  return (
    <GlassCard
      ref={ref}
      sx={{
        mb: 3,
        p: 2,
        borderLeft: `4px solid ${config.color}`,
        background: 'rgba(255, 255, 255, 0.1)',
        display: 'flex',
        gap: 1.5,
        alignItems: 'flex-start',
        ...props.sx,
      }}
      {...props}
    >
      {config.icon}
      <Box sx={{ flex: 1 }}>
        {title && (
          <Typography
            variant="h6"
            sx={{
              mb: 1,
              color: 'rgba(255, 255, 255, 1)',
              fontWeight: 600
            }}
          >
            {title}
          </Typography>
        )}
        <Box sx={{ color: 'rgba(255, 255, 255, 0.95)' }}>
          {typeof children === 'string' ? (
            <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.95)' }}>
              {children}
            </Typography>
          ) : (
            children
          )}
        </Box>
      </Box>
    </GlassCard>
  );
});

// Glass Button - Button with glass effect
export const GlassButton = forwardRef(({
  children,
  variant: customVariant = 'glass',
  size: customSize = 'medium',
  sx,
  ...props
}, ref) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const variants = {
    glass: {
      border: glassTheme.glass.border,
      background: glassTheme.glass.background,
      backdropFilter: glassTheme.glass.backdropFilter,
      color: glassTheme.colors.textPrimary,
      '&:hover': {
        background: glassTheme.glass.backgroundHover,
      },
      '&:disabled': {
        background: 'rgba(255, 255, 255, 0.05)',
        color: 'rgba(255, 255, 255, 0.4)',
        cursor: 'not-allowed',
      },
    },
    solid: {
      height: '48px',
      width: '100%',
      cursor: 'pointer',
      borderRadius: '6px',
      background: 'white',
      padding: '0 8px',
      fontSize: '0.875rem',
      fontWeight: 500,
      color: 'rgba(39, 39, 42, 1)',
      boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 8px 10px -6px rgba(0, 0, 0, 0.1)',
      textTransform: 'none',
      border: 'none',
      '&:hover': {
        background: 'rgba(255, 255, 255, 0.9)',
      },
      '&:disabled': {
        background: 'rgba(255, 255, 255, 0.5)',
        color: 'rgba(39, 39, 42, 0.5)',
      },
    },
  };

  const sizes = {
    small: {
      padding: isMobile ? '8px 16px' : '10px 20px',
      fontSize: isMobile
        ? glassTheme.fontSize.mobile.body2
        : glassTheme.fontSize.desktop.body2,
    },
    medium: {
      padding: isMobile ? '12px 24px' : '14px 32px',
      fontSize: isMobile
        ? glassTheme.fontSize.mobile.body1
        : glassTheme.fontSize.desktop.body1,
    },
    large: {
      padding: isMobile ? '16px 32px' : '18px 40px',
      fontSize: isMobile
        ? glassTheme.fontSize.mobile.h4
        : glassTheme.fontSize.desktop.h4,
    },
  };

  return (
    <Button
      ref={ref}
      variant="text"
      disableElevation
      disableRipple
      sx={{
        textTransform: 'none',
        borderRadius: glassTheme.borderRadius.small,
        fontWeight: 500,
        transition: `all ${glassTheme.transitions.standard}`,
        ...sizes[customSize],
        ...variants[customVariant],
        ...sx,
      }}
      {...props}
    >
      {children}
    </Button>
  );
});

// Glass Icon Button - Icon button with glass effect
export const GlassIconButton = forwardRef(({ children, ...props }, ref) => {
  return (
    <IconButton
      ref={ref}
      sx={{
        color: glassTheme.colors.textPrimary,
        border: glassTheme.glass.borderStrong,
        background: glassTheme.glass.backgroundDense,
        backdropFilter: glassTheme.glass.backdropFilterLight,
        transition: `all ${glassTheme.transitions.standard}`,
        '&:hover': {
          background: glassTheme.glass.backgroundHover,
        },
        ...props.sx,
      }}
      {...props}
    >
      {children}
    </IconButton>
  );
});

// Glass Chip - Chip component with glass effect
export const GlassChip = forwardRef(({
  variant = 'default',
  children,
  ...props
}, ref) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const variants = {
    default: {
      background: 'rgba(255, 255, 255, 0.25)',
      border: '1px solid rgba(255, 255, 255, 0.35)',
      color: 'white',
      fontWeight: 500,
    },
    admin: {
      background: 'rgba(255, 255, 255, 0.3)',
      border: '1px solid rgba(255, 255, 255, 0.4)',
      color: 'white',
      fontWeight: 500,
    },
    deceased: {
      background: 'rgba(211, 47, 47, 0.35)',
      border: '1px solid rgba(211, 47, 47, 0.5)',
      color: 'white',
      fontWeight: 500,
    },
    success: {
      background: 'rgba(76, 175, 80, 0.4)',
      border: '1px solid rgba(76, 175, 80, 0.6)',
      color: 'white',
      fontWeight: 500,
    },
    info: {
      background: 'rgba(33, 150, 243, 0.4)',
      border: '1px solid rgba(33, 150, 243, 0.6)',
      color: 'white',
      fontWeight: 500,
    },
    warning: {
      background: 'rgba(255, 152, 0, 0.4)',
      border: '1px solid rgba(255, 152, 0, 0.6)',
      color: 'white',
      fontWeight: 500,
    },
    error: {
      background: 'rgba(211, 47, 47, 0.35)',
      border: '1px solid rgba(211, 47, 47, 0.5)',
      color: 'white',
      fontWeight: 500,
    },
    secondary: {
      background: 'rgba(156, 39, 176, 0.4)',
      border: '1px solid rgba(156, 39, 176, 0.6)',
      color: 'white',
      fontWeight: 500,
    },
  };

  return (
    <Chip
      ref={ref}
      label={children}
      size={isMobile ? 'small' : 'medium'}
      sx={{
        ...variants[variant],
        ...props.sx,
      }}
      {...props}
    />
  );
});

// Glass Input - Input field with glass effect
export const GlassInput = forwardRef(({ ...props }, ref) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  return (
    <Box sx={{ position: 'relative', height: '44px', overflow: 'hidden', mb: 2 }}>
      <Box
        component="input"
        ref={ref}
        sx={{
          position: 'relative',
          zIndex: 1,
          height: '100%',
          width: '100%',
          borderRadius: glassTheme.borderRadius.small,
          border: '1px solid rgba(255, 255, 255, 0.12)',
          background: 'rgba(255, 255, 255, 0.06)',
          padding: '0 16px',
          color: glassTheme.colors.textPrimary,
          fontSize: isMobile
            ? glassTheme.fontSize.mobile.body1
            : glassTheme.fontSize.desktop.body2,
          outline: 'none',
          transition: `all ${glassTheme.transitions.standard}`,
          '&::placeholder': {
            color: 'rgba(255, 255, 255, 0.2)',
          },
          '&:focus': {
            outline: 'none',
          },
          '&:focus + .focus-line': {
            opacity: 0.4,
          },
          '&:focus + .focus-line + .focus-glow': {
            transform: 'scaleY(1)',
            opacity: 0.3,
          },
          ...props.sx,
        }}
        {...props}
      />
      <Box
        className="focus-line"
        sx={{
          position: 'absolute',
          bottom: 0,
          left: 0,
          zIndex: 2,
          height: '1px',
          width: '100%',
          background: 'linear-gradient(to right, transparent 5%, white 50%, transparent 95%)',
          opacity: 0,
          transition: `opacity ${glassTheme.transitions.standard}`,
        }}
      />
      <Box
        className="focus-glow"
        sx={{
          position: 'absolute',
          bottom: 0,
          left: '16px',
          right: '16px',
          zIndex: 1,
          height: '16px',
          transformOrigin: 'bottom',
          transform: 'scaleY(0) skewX(-12deg)',
          background: 'linear-gradient(to bottom, white, transparent)',
          opacity: 0,
          filter: 'blur(12px)',
          transition: `all ${glassTheme.transitions.standard}`,
        }}
      />
    </Box>
  );
});

// Glass Search Box - Enhanced search input with glass effect and search icon
export const GlassSearchBox = forwardRef(({
  placeholder = "Search...",
  value = "",
  onChange,
  onClear,
  showClearButton = true,
  ...props
}, ref) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const handleClear = () => {
    if (onClear) {
      onClear();
    } else if (onChange) {
      onChange({ target: { value: '' } });
    }
  };

  return (
    <Box
      ref={ref}
      sx={{
        position: 'relative',
        height: '48px',
        overflow: 'hidden',
        mb: 3,
        ...props.sx
      }}
      {...props}
    >
      <Box
        component="input"
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        sx={{
          position: 'relative',
          zIndex: 1,
          height: '100%',
          width: '100%',
          borderRadius: glassTheme.borderRadius.medium,
          border: glassTheme.glass.border,
          background: glassTheme.glass.background,
          backdropFilter: glassTheme.glass.backdropFilter,
          padding: '0 48px 0 48px', // Space for search icon on left and clear on right
          color: glassTheme.colors.textPrimary,
          fontSize: isMobile
            ? glassTheme.fontSize.mobile.body1
            : glassTheme.fontSize.desktop.body1,
          outline: 'none',
          transition: `all ${glassTheme.transitions.standard}`,
          '&::placeholder': {
            color: 'rgba(255, 255, 255, 0.4)',
          },
          '&:focus': {
            outline: 'none',
            border: '1px solid rgba(255, 255, 255, 0.3)',
            background: glassTheme.glass.backgroundHover,
          },
          '&:focus + .search-glow': {
            opacity: 0.6,
            transform: 'scaleY(1)',
          },
        }}
      />

      {/* Search Icon */}
      <Box
        sx={{
          position: 'absolute',
          left: '16px',
          top: '50%',
          transform: 'translateY(-50%)',
          zIndex: 2,
          color: 'rgba(255, 255, 255, 0.5)',
          pointerEvents: 'none',
        }}
      >
        <Search fontSize="small" />
      </Box>

      {/* Clear Button */}
      {showClearButton && value && (
        <IconButton
          onClick={handleClear}
          size="small"
          sx={{
            position: 'absolute',
            right: '8px',
            top: '50%',
            transform: 'translateY(-50%)',
            zIndex: 2,
            color: 'rgba(255, 255, 255, 0.5)',
            padding: '4px',
            '&:hover': {
              color: 'rgba(255, 255, 255, 0.8)',
              backgroundColor: 'rgba(255, 255, 255, 0.1)',
            },
          }}
        >
          <Clear fontSize="small" />
        </IconButton>
      )}

      {/* Focus Glow Effect */}
      <Box
        className="search-glow"
        sx={{
          position: 'absolute',
          bottom: 0,
          left: '16px',
          right: '16px',
          zIndex: 1,
          height: '20px',
          transformOrigin: 'bottom',
          transform: 'scaleY(0) skewX(-8deg)',
          background: 'linear-gradient(to bottom, rgba(255, 255, 255, 0.3), transparent)',
          opacity: 0,
          filter: 'blur(8px)',
          transition: `all ${glassTheme.transitions.standard}`,
        }}
      />
    </Box>
  );
});

// Glass Toggle - Toggle button with glass effect
export const GlassToggle = forwardRef(({
  label,
  checked = false,
  onChange,
  icon,
  checkedIcon,
  size = 'medium',
  ...props
}, ref) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const handleToggle = () => {
    if (onChange) {
      onChange(!checked);
    }
  };

  const sizes = {
    small: {
      height: '36px',
      padding: '8px 16px',
      fontSize: isMobile ? '0.8rem' : '0.875rem',
      iconSize: '16px',
    },
    medium: {
      height: '44px',
      padding: '10px 20px',
      fontSize: isMobile ? '0.9rem' : '1rem',
      iconSize: '18px',
    },
    large: {
      height: '52px',
      padding: '14px 24px',
      fontSize: isMobile ? '1rem' : '1.125rem',
      iconSize: '20px',
    },
  };

  const sizeStyles = sizes[size];

  return (
    <Box
      ref={ref}
      onClick={handleToggle}
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 1.5,
        height: sizeStyles.height,
        padding: sizeStyles.padding,
        borderRadius: glassTheme.borderRadius.medium,
        border: checked ?
          '1px solid rgba(255, 255, 255, 0.3)' :
          glassTheme.glass.border,
        background: checked ?
          glassTheme.glass.backgroundHover :
          glassTheme.glass.background,
        backdropFilter: glassTheme.glass.backdropFilter,
        cursor: 'pointer',
        userSelect: 'none',
        transition: `all ${glassTheme.transitions.standard}`,
        color: checked ?
          glassTheme.colors.textPrimary :
          'rgba(255, 255, 255, 0.7)',
        '&:hover': {
          background: glassTheme.glass.backgroundHover,
          border: '1px solid rgba(255, 255, 255, 0.25)',
          color: glassTheme.colors.textPrimary,
        },
        ...props.sx,
      }}
      {...props}
    >
      {/* Icon */}
      {(icon || checkedIcon) && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            fontSize: sizeStyles.iconSize,
            transition: `all ${glassTheme.transitions.standard}`,
          }}
        >
          {checked && checkedIcon ? checkedIcon : icon}
        </Box>
      )}

      {/* Label */}
      {label && (
        <Typography
          variant="body2"
          sx={{
            fontSize: sizeStyles.fontSize,
            fontWeight: 500,
            color: 'inherit',
          }}
        >
          {label}
        </Typography>
      )}
    </Box>
  );
});

// Shimmer Effect Component
export const GlassShimmer = forwardRef((props, ref) => {
  return (
    <Box
      ref={ref}
      sx={{
        position: 'fixed',
        top: 0,
        left: 0,
        zIndex: 1,
        height: '128px',
        width: '128px',
        borderRadius: '50%',
        background: 'white',
        filter: 'blur(70px)',
        transition: `opacity ${glassTheme.transitions.standard}`,
        opacity: 0,
        pointerEvents: 'none',
        ...props.sx,
      }}
      {...props}
    />
  );
});

// Hook for shimmer effect
export const useGlassShimmer = (targetSelector = '.glass-card, .glass-container') => {
  const shimmerRef = React.useRef(null);

  React.useEffect(() => {
    const handleMouseMove = (e) => {
      const { clientX, clientY } = e;
      if (shimmerRef.current) {
        if (e.target.closest(targetSelector)) {
          shimmerRef.current.style.setProperty(
            "transform",
            `translateX(${clientX - 64}px) translateY(${clientY - 64}px)`
          );
          shimmerRef.current.style.setProperty("opacity", "0.4");
        } else {
          shimmerRef.current.style.setProperty("opacity", "0");
        }
      }
    };

    document.body.addEventListener("mousemove", handleMouseMove);
    return () => document.body.removeEventListener("mousemove", handleMouseMove);
  }, [targetSelector]);

  return shimmerRef;
};

// Glass Navigation Button - For complex navigation buttons with icons
export const GlassNavigationButton = forwardRef(({
  children,
  icon,
  label,
  onClick,
  ...props
}, ref) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  return (
    <Box
      className="shimmer-button"
      sx={{
        position: 'relative',
        overflow: 'hidden',
        borderRadius: glassTheme.borderRadius.medium,
        border: glassTheme.glass.border,
        background: glassTheme.glass.background,
        backdropFilter: glassTheme.glass.backdropFilter,
        boxShadow: glassTheme.glass.shadowLight,
        ...props.sx,
      }}
    >
      <Button
        ref={ref}
        onClick={onClick}
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: isMobile ? 0 : 1.5,
          padding: isMobile ? '12px' : '32px 48px',
          color: 'white',
          textTransform: 'none',
          fontSize: isMobile ? '0.9rem' : '1.2rem',
          fontWeight: 500,
          width: isMobile ? '60px' : '180px',
          height: isMobile ? '60px' : '120px',
          minWidth: isMobile ? '60px' : '180px',
          minHeight: isMobile ? '60px' : '120px',
          borderRadius: glassTheme.borderRadius.medium,
          '&:hover': {
            backgroundColor: 'rgba(255, 255, 255, 0.05)',
          },
        }}
        {...props}
      >
        {React.cloneElement(icon, {
          sx: {
            fontSize: isMobile ? '2rem' : '2.5rem',
            ...icon.props.sx
          }
        })}
        {!isMobile && label}
      </Button>
    </Box>
  );
});

// Glass Text Button - For subtle text-only buttons
export const GlassTextButton = forwardRef(({ children, ...props }, ref) => {
  return (
    <Button
      ref={ref}
      sx={{
        textTransform: 'none',
        color: 'rgba(161, 161, 170, 1)',
        fontSize: '0.875rem',
        '&:hover': {
          backgroundColor: 'transparent',
          color: 'white',
        },
        ...props.sx,
      }}
      {...props}
    >
      {children}
    </Button>
  );
});

// Glass Tab Selector - For tab switching interfaces
export const GlassTabSelector = forwardRef(({
  tabs,
  activeTab,
  onTabChange,
  ...props
}, ref) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  return (
    <Box
      ref={ref}
      sx={{
        position: 'relative',
        display: 'flex',
        width: '100%',
        maxWidth: isMobile ? '320px' : '360px',
        height: isMobile ? '48px' : '56px',
        mb: isMobile ? 2 : 4,
        borderRadius: glassTheme.borderRadius.medium,
        border: glassTheme.glass.border,
        background: glassTheme.glass.background,
        backdropFilter: glassTheme.glass.backdropFilter,
        boxShadow: glassTheme.glass.shadowLight,
        overflow: 'hidden',
        ...props.sx,
      }}
      {...props}
    >
      {/* Sliding background indicator */}
      <Box
        sx={{
          position: 'absolute',
          top: '4px',
          left: activeTab === tabs[0].value ? '4px' : 'calc(50% + 2px)',
          height: 'calc(100% - 8px)',
          width: 'calc(50% - 6px)',
          borderRadius: '4px',
          border: '1px solid rgba(255, 255, 255, 0.2)',
          background: 'rgba(255, 255, 255, 0.12)',
          backdropFilter: glassTheme.glass.backdropFilter,
          boxShadow: 'inset 0 1px 0 rgba(255, 255, 255, 0.15), 0 2px 8px rgba(0, 0, 0, 0.1)',
          transition: `left ${glassTheme.transitions.standard} ${glassTheme.transitions.easing}`,
          zIndex: 1,
        }}
      />

      {tabs.map((tab, index) => (
        <Box
          key={tab.value}
          onClick={() => onTabChange(tab.value)}
          sx={{
            position: 'relative',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: '50%',
            height: '100%',
            cursor: 'pointer',
            zIndex: 2,
            fontSize: '0.9rem',
            fontWeight: 500,
            transition: `color ${glassTheme.transitions.standard} ease`,
            color: activeTab === tab.value ? 'white' : 'rgba(255, 255, 255, 0.6)',
          }}
        >
          {tab.label}
        </Box>
      ))}
    </Box>
  );
});

// Glass Dialog - Dialog with glass morphism styling
export const GlassDialog = forwardRef(({
  open,
  onClose,
  title,
  children,
  actions,
  maxWidth = 'sm',
  fullWidth = true,
  ...props
}, ref) => {
  return (
    <Dialog
      ref={ref}
      open={open}
      onClose={onClose}
      maxWidth={maxWidth}
      fullWidth={fullWidth}
      PaperProps={{
        sx: {
          background: 'linear-gradient(135deg, rgba(30, 30, 50, 0.95) 0%, rgba(20, 20, 35, 0.95) 100%)',
          backdropFilter: 'blur(20px)',
          border: '1px solid rgba(255, 255, 255, 0.15)',
          borderRadius: glassTheme.borderRadius.large,
          boxShadow: '0 8px 32px 0 rgba(0, 0, 0, 0.5), inset 0 1px 0 rgba(255, 255, 255, 0.1)',
          color: 'white',
        }
      }}
      BackdropProps={{
        sx: {
          backdropFilter: 'blur(8px)',
          backgroundColor: 'rgba(0, 0, 0, 0.6)',
        }
      }}
      {...props}
    >
      {title && (
        <DialogTitle sx={{
          color: 'white',
          fontWeight: 600,
          borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
          pb: 2,
          background: 'rgba(255, 255, 255, 0.03)',
        }}>
          {title}
        </DialogTitle>
      )}
      {children && (
        <DialogContent sx={{ pt: 3, color: 'rgba(255, 255, 255, 0.95)' }}>
          {children}
        </DialogContent>
      )}
      {actions && (
        <DialogActions sx={{
          borderTop: '1px solid rgba(255, 255, 255, 0.1)',
          pt: 2,
          px: 3,
          pb: 2,
          background: 'rgba(255, 255, 255, 0.03)',
        }}>
          {actions}
        </DialogActions>
      )}
    </Dialog>
  );
});

// Glass TextField - TextField with glass styling
export const GlassTextField = forwardRef(({ ...props }, ref) => {
  return (
    <TextField
      ref={ref}
      sx={{
        '& .MuiOutlinedInput-root': {
          color: 'white',
          background: 'rgba(255, 255, 255, 0.08)',
          backdropFilter: 'blur(10px)',
          borderRadius: glassTheme.borderRadius.small,
          transition: 'all 0.2s ease',
          boxShadow: 'inset 0 1px 3px rgba(0, 0, 0, 0.2)',
          '& fieldset': {
            borderColor: 'rgba(255, 255, 255, 0.25)',
            borderRadius: glassTheme.borderRadius.small,
            transition: 'all 0.2s ease',
          },
          '&:hover': {
            background: 'rgba(255, 255, 255, 0.08)',
            '& fieldset': {
              borderColor: 'rgba(255, 255, 255, 0.35)',
            },
          },
          '&.Mui-focused': {
            background: 'rgba(255, 255, 255, 0.08)',
            boxShadow: 'inset 0 1px 3px rgba(0, 0, 0, 0.2), 0 0 0 2px rgba(255, 255, 255, 0.1)',
            '& fieldset': {
              borderColor: 'rgba(255, 255, 255, 0.5)',
            },
          },
          '&.Mui-disabled': {
            background: 'rgba(255, 255, 255, 0.03)',
            '& fieldset': {
              borderColor: 'rgba(255, 255, 255, 0.1)',
            },
          },
          '&.Mui-error': {
            background: 'rgba(255, 255, 255, 0.08)',
            '& fieldset': {
              borderColor: 'rgba(211, 47, 47, 0.5)',
            },
          },
          '& input': {
            color: 'white',
            '&::placeholder': {
              color: 'rgba(255, 255, 255, 0.4)',
              opacity: 1,
            },
          },
          '& textarea': {
            color: 'white',
            background: 'transparent',
            '&::placeholder': {
              color: 'rgba(255, 255, 255, 0.4)',
              opacity: 1,
            },
          },
          '&.MuiInputBase-multiline': {
            background: 'rgba(255, 255, 255, 0.08)',
          },
          '& .MuiInputAdornment-root': {
            color: 'rgba(255, 255, 255, 0.6)',
          },
        },
        '& .MuiInputLabel-root': {
          color: 'rgba(255, 255, 255, 0.7)',
        },
        '& .MuiInputLabel-root.Mui-focused': {
          color: 'rgba(255, 255, 255, 0.95)',
        },
        '& .MuiInputLabel-root.Mui-disabled': {
          color: 'rgba(255, 255, 255, 0.4)',
        },
        '& .MuiFormHelperText-root': {
          color: 'rgba(255, 255, 255, 0.65)',
          marginLeft: '4px',
        },
        '& .MuiFormHelperText-root.Mui-error': {
          color: '#ef5350',
        },
        ...props.sx,
      }}
      {...props}
    />
  );
});

// Export layout components for convenience
export {
  GlassPageHeader,
  GlassProfileCard,
  GlassProfileGrid,
  GlassStatusText,
} from './GlassLayoutComponents';

const GlassComponentsExport = {
  GlassBackground,
  GlassContainer,
  GlassCard,
  GlassAlert,
  GlassInfoCard,
  GlassWarningCard,
  GlassButton,
  GlassIconButton,
  GlassChip,
  GlassInput,
  GlassSearchBox,
  GlassToggle,
  GlassShimmer,
  GlassNavigationButton,
  GlassTextButton,
  GlassTabSelector,
  GlassDialog,
  GlassTextField,
  useGlassShimmer,
};

export default GlassComponentsExport;